/**
 * @file ContactModal.js
 * @version 2.19.3
 * @author Cam Seymour
 * @project Excel Agri System
 * @created 2025-09-21
 * @modified 2026-10-30
 *
 * 2.19.2 (2026-10-30): the phone number's display and tel: link come from
 * utils/phoneNumber (moved there unchanged), which the rep phone's customer
 * card (RepContactCard) uses too.
 * 2.19.1 (2026-10-28): the Contracts tab's "See all contracts" opens My
 * contracts narrowed to this customer (its filters set in the nav store).
 * 2.19.0 (2026-10-28, Cam — rep mobile review): on a rep's phone the window
 * has tabs under the customer's name — Details · Contracts · Orders ·
 * Invoices (RepContactTabs) — so the contracts, orders and invoices the
 * laptop shows in the slide-out panel can be reached on the phone too (they
 * could not at all). Read only. The laptop is unchanged.
 *
 * 2.18.2 (2026-10-08): milk powder prices in the contracts view show their
 * From – To dates (DD/MM/YY) and sit under every pricing period they touch.
 *
 * 2.18.1 (2026-10-05): the Bills panel shows statuses as Draft / Approved /
 * Paid (utils/billStatus.billStatusLabel); its filter offers those.
 */

import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { API_URL } from '../config';
import useRepMobile from '../hooks/useRepMobile';
import { RepContactTabBar, RepContactContracts, RepContactOrders, RepContactInvoices } from './RepContactTabs';
import { phoneTelHref, phoneDisplay } from '../utils/phoneNumber';
import './ContactModal.css';
import './TableStyles.css';
import StatementsPanel from './StatementsPanel';
import PaymentBehaviourPanel from './PaymentBehaviourPanel';
import PrescriptionsPanel from './PrescriptionsPanel';
import SupplierHaulageRatesPanel from './SupplierHaulageRatesPanel';
import CustomerFormulationsPanel from './CustomerFormulationsPanel';
import { isPrescriptionInUse } from '../utils/prescriptions';
import { formatDateShort } from '../utils/dateFormatters';
import { rangeOf as milkRangeOf, formatRange as formatMilkRange } from '../utils/milkPowderDates';
import { formatAddressFromFlat as formatAddressFromFlatUtil } from '../utils/formatAddress';
import { getCurrentPeriodId } from '../utils/periodHelper';
import { getTaxPercentDisplay } from '../utils/taxTypes';
import { formatPrice } from '../utils/currencyFormatter';
import { formatQuantity } from '../utils/numberFormatters';
import PaginationControls from './PaginationControls';
import Dropdown from './Dropdown';
import DateInput from './DateInput';
import Input from './Input';
import SearchHelp from './SearchHelp';
import useLatestRequest from '../hooks/useLatestRequest';
import AuditHistoryModal from './AuditHistoryModal';
import ContactEditModal from './ContactEditModal';
import { useSlideOutButton } from './SlideOutButton';
import useReadOnly from '../hooks/useReadOnly';
import { useEntityModal } from './EntityModalProvider';
import useViewport from '../hooks/useViewport';
import ContactNotesModal from './ContactNotesModal';
import CustomIcon from './CustomIcon';
import TickCheckbox from './TickCheckbox';
import useAccess from '../hooks/useAccess';
import { revealInSearch } from '../utils/revealInSearch';
import FindInSearchButton from './FindInSearchButton';
import { useNavStore } from '../store/navStore';
import { defaultFilters as defaultRepContractFilters } from '../utils/repContractsView';
import { billStatusLabel } from '../utils/billStatus';

/**
 * Where each right-hand panel's list lives as a full screen (and the tab it
 * is on, for a page with more than one).
 *
 * Handed over with utils/revealInSearch.js — the same handoff as every "Find
 * in …" — so the screen opens with the search already run, from page 1.
 * Searching the ACCOUNT NUMBER
 * rather than the company name because it is exact: "Jones" finds eleven
 * farms, E0015 finds one.
 *
 * A panel with no entry here simply shows no link — Contracts, Prescriptions,
 * Credit, Statements and Complaints have no per-contact search screen to send
 * anyone to. The permission gate below decides which of the rest this user can
 * actually open.
 */
const PANEL_SEARCH_TARGETS = {
  invoices: { menu: 'search-invoices', label: 'Invoices' },
  'purchase-orders': { menu: 'search-purchase-orders', tab: 'orders', label: 'Purchase Orders' },
  bills: { menu: 'search-bills', label: 'Bills' }
};

/**
 * The Summer / Winter pricing periods a milk powder price's From – To touches
 * (Summer 1 May – 30 Sep, Winter 1 Oct – 30 Apr), so the contracts view's
 * period selector finds it in each. A price used to be one month, so one period.
 */
function milkPowderSeasons(row) {
  const range = milkRangeOf(row);
  if (!range) return [];
  const seasonOf = (day) => {
    const year = Number(day.slice(0, 4));
    const month = Number(day.slice(5, 7));
    if (month >= 5 && month <= 9) {
      return { name: `Summer ${year}`, start_date: `${year}-05-01`, end_date: `${year}-09-30` };
    }
    const w = month >= 10 ? year : year - 1;
    return { name: `Winter ${w}-${String(w + 1).slice(-2)}`, start_date: `${w}-10-01`, end_date: `${w + 1}-04-30` };
  };
  const out = [];
  let season = seasonOf(range.from);
  // Walk season by season to the To date (a price rarely spans more than two).
  for (let guard = 0; guard < 20; guard += 1) {
    out.push(season);
    if (season.end_date >= range.to) break;
    const y = Number(season.end_date.slice(0, 4));
    const next = season.end_date.slice(5) === '09-30' ? `${y}-10-01` : `${y}-05-01`;
    season = seasonOf(next);
  }
  return out;
}

function ContactModal({ contactId, onClose, onEdit, onNavigate }) {
  const access = useAccess();
  // Read from the data-readonly attribute Dashboard maintains on <body>, so
  // this works wherever the modal is opened from without threading `user`
  // through every call site.
  const readOnly = useReadOnly();
  const { isPhone } = useViewport();
  // Opens the invoice / purchase order modals from the numbers in the right
  // panels. Degrades to a no-op outside the provider rather than throwing, so
  // the numbers simply stay plain text if this is ever rendered on its own.
  const { openEntity } = useEntityModal();
  // A rep's phone: tabs in place of the slide-out panel (2.19.0).
  const repMobile = useRepMobile();
  const [showNotesEdit, setShowNotesEdit] = useState(false);
  const [contact, setContact] = useState(null);
  const [loading, setLoading] = useState(true);
  const [invoices, setInvoices] = useState([]);
  const [purchaseOrders, setPurchaseOrders] = useState([]);
  const [creditUsage, setCreditUsage] = useState(null);
  const [, setCreditLoading] = useState(false);
  const [closing, setClosing] = useState(false);
  const [editingCredit, setEditingCredit] = useState(false);
  const [newCreditLimit, setNewCreditLimit] = useState('');
  const [creditNotes, setCreditNotes] = useState('');

  // Panel state - which right panel is open (null = collapsed)
  const [activePanel, setActivePanel] = useState(null);
  const [isPanelOpening, setIsPanelOpening] = useState(false);
  const prevActivePanelRef = useRef(null);

  // Invoice state — default to all so the modal isn't capped at 6 months
  const [showAllInvoices, setShowAllInvoices] = useState(true);
  const [invoicePage, setInvoicePage] = useState(1);
  const [invoiceSortField, setInvoiceSortField] = useState('date');
  const [invoiceSortDirection, setInvoiceSortDirection] = useState('desc');
  const [invoiceTotalCount, setInvoiceTotalCount] = useState(0);
  const [invoiceSearch, setInvoiceSearch] = useState('');
  const [invoiceStatus, setInvoiceStatus] = useState('');
  const [invoiceDateFrom, setInvoiceDateFrom] = useState('');
  const [invoiceDateTo, setInvoiceDateTo] = useState('');

  // PO state — default to all so the modal isn't capped at 6 months
  const [showAllPOs, setShowAllPOs] = useState(true);
  const [poPage, setPoPage] = useState(1);
  const [poSortField, setPoSortField] = useState('date');
  const [poSortDirection, setPoSortDirection] = useState('desc');
  const [poTotalCount, setPoTotalCount] = useState(0);
  const [poSearch, setPoSearch] = useState('');
  const [poStatus, setPoStatus] = useState('');
  const [poDateFrom, setPoDateFrom] = useState('');
  const [poDateTo, setPoDateTo] = useState('');

  // Bills state
  const [bills, setBills] = useState([]);
  const [billsPage, setBillsPage] = useState(1);
  const [billsSortField, setBillsSortField] = useState('bill_date');
  const [billsSortDirection, setBillsSortDirection] = useState('desc');
  const [billsTotalCount, setBillsTotalCount] = useState(0);
  const [billsSearch, setBillsSearch] = useState('');
  const [billsStatus, setBillsStatus] = useState('');
  const [billsDateFrom, setBillsDateFrom] = useState('');
  const [billsDateTo, setBillsDateTo] = useState('');
  const [expandedBills, setExpandedBills] = useState(new Set());
  const [billLines, setBillLines] = useState({});

  // Expandable rows state
  const [expandedInvoices, setExpandedInvoices] = useState(new Set());
  const [expandedPOs, setExpandedPOs] = useState(new Set());
  const [expandedStraights, setExpandedStraights] = useState(false);
  const [invoiceLines, setInvoiceLines] = useState({});
  const [poLines, setPoLines] = useState({});
  const [loadingLines, setLoadingLines] = useState({});
  const [expandingAllInvoices, setExpandingAllInvoices] = useState(false);
  const [expandingAllPOs, setExpandingAllPOs] = useState(false);
  const [expandingAllBills, setExpandingAllBills] = useState(false);

  // Contracts state
  const [contracts, setContracts] = useState([]);
  const [periods, setPeriods] = useState([]);
  const [selectedPeriod, setSelectedPeriod] = useState('');
  const [contractsLoading, setContractsLoading] = useState(false);

  // Complaints state — surfaces both customer-side and supplier-side complaints
  // for this contact (account_id OR supplier_id matches).
  const [complaints, setComplaints] = useState([]);
  const [complaintsLoading, setComplaintsLoading] = useState(false);

  // Prescriptions — loaded with the contact rather than when the panel opens,
  // so the nav button can show how many are in use before anyone clicks it.
  const [prescriptions, setPrescriptions] = useState([]);
  const [prescriptionsLoading, setPrescriptionsLoading] = useState(false);
  const [prescriptionsError, setPrescriptionsError] = useState(null);
  const prescriptionsInUse = prescriptions.filter(isPrescriptionInUse).length;

  // History modal state
  const [showHistory, setShowHistory] = useState(false);

  // Internal edit modal — used when the parent screen doesn't supply its own
  // onEdit handler, so the Edit button works from any screen that shows a contact.
  const [showInternalEdit, setShowInternalEdit] = useState(false);

  const ITEMS_PER_PAGE = 20;

  // Slideout buttons - re-measure when badge counts change
  useSlideOutButton([invoiceTotalCount, poTotalCount, contracts.length, billsTotalCount, complaints.length, prescriptionsInUse]);

  // Track if panel is opening (from closed) vs switching (already open)
  useEffect(() => {
    const wasOpen = prevActivePanelRef.current !== null;
    const isOpening = !wasOpen && activePanel !== null;
    setIsPanelOpening(isOpening);
    prevActivePanelRef.current = activePanel;
  }, [activePanel]);

  useEffect(() => {
    if (contactId) {
      fetchContactDetails();
      fetchContactInvoices();
      fetchPurchaseOrders();
      fetchBills();
      fetchComplaints();
      fetchPrescriptions();
      fetchCreditUsage();
      document.body.classList.add('modal-open');
      const contentWrapper = document.querySelector('.content-wrapper');
      if (contentWrapper) {
        contentWrapper.classList.add('modal-open');
      }
    }

    return () => {
      document.body.classList.remove('modal-open');
      const contentWrapper = document.querySelector('.content-wrapper');
      if (contentWrapper) {
        contentWrapper.classList.remove('modal-open');
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contactId]);

  // Fetch periods when contact is loaded
  useEffect(() => {
    if (contact && periods.length === 0) {
      fetchPeriods();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contact]);

  // The three tab searches fetch as you type: 300ms after the last keystroke,
  // and only the newest response may write each tab's rows.
  const latestInvoices = useLatestRequest();
  const latestPOs = useLatestRequest();
  const latestBills = useLatestRequest();

  useEffect(() => {
    const t = setTimeout(fetchContactInvoices, invoiceSearch ? 300 : 0);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [invoicePage, invoiceSortField, invoiceSortDirection, showAllInvoices, invoiceSearch, invoiceStatus, invoiceDateFrom, invoiceDateTo]);

  useEffect(() => {
    const t = setTimeout(fetchPurchaseOrders, poSearch ? 300 : 0);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [poPage, poSortField, poSortDirection, showAllPOs, poSearch, poStatus, poDateFrom, poDateTo]);

  useEffect(() => {
    if (!contactId) return undefined;
    const t = setTimeout(fetchBills, billsSearch ? 300 : 0);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [billsPage, billsSortField, billsSortDirection, billsSearch, billsStatus, billsDateFrom, billsDateTo]);

  // Fetch periods when contracts panel is opened
  useEffect(() => {
    if (activePanel === 'contracts' && periods.length === 0) {
      fetchPeriods();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activePanel]);

  // Fetch contracts when panel is opened or period changes
  useEffect(() => {
    if (activePanel === 'contracts' && contact && selectedPeriod) {
      fetchContractPricing();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activePanel, selectedPeriod, contact]);

  const fetchContactDetails = async () => {
    try {
      const response = await fetch(`${API_URL}/api/contacts/${contactId}`, {
        credentials: 'include'
      });
      const data = await response.json();
      setContact(data);
    } catch (error) {
      console.error('Failed to fetch contact:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchContactInvoices = async () => {
    const seq = latestInvoices.begin();
    try {
      const params = new URLSearchParams({
        showAll: (showAllInvoices || !!invoiceSearch || !!invoiceStatus || !!invoiceDateFrom || !!invoiceDateTo).toString(),
        page: invoicePage.toString(),
        limit: ITEMS_PER_PAGE.toString(),
        sortBy: invoiceSortField,
        sortDir: invoiceSortDirection,
        ...(invoiceSearch ? { search: invoiceSearch } : {}),
        ...(invoiceStatus ? { status: invoiceStatus } : {}),
        ...(invoiceDateFrom ? { dateFrom: invoiceDateFrom } : {}),
        ...(invoiceDateTo ? { dateTo: invoiceDateTo } : {})
      });

      const response = await fetch(`${API_URL}/api/contacts/${contactId}/invoices?${params}`, {
        credentials: 'include'
      });
      const data = await response.json();
      if (!latestInvoices.isLatest(seq)) return;   // superseded by a later search
      setInvoices(data.invoices || []);
      setInvoiceTotalCount(data.total || 0);
    } catch (error) {
      if (!latestInvoices.isLatest(seq)) return;
      console.error('Failed to fetch invoices:', error);
    }
  };

  const fetchPurchaseOrders = async () => {
    const seq = latestPOs.begin();
    try {
      const params = new URLSearchParams({
        showAll: (showAllPOs || !!poSearch || !!poStatus || !!poDateFrom || !!poDateTo).toString(),
        page: poPage.toString(),
        limit: ITEMS_PER_PAGE.toString(),
        sortBy: poSortField,
        sortDir: poSortDirection,
        ...(poSearch ? { search: poSearch } : {}),
        ...(poStatus ? { status: poStatus } : {}),
        ...(poDateFrom ? { dateFrom: poDateFrom } : {}),
        ...(poDateTo ? { dateTo: poDateTo } : {})
      });

      const response = await fetch(`${API_URL}/api/contacts/${contactId}/purchase-orders?${params}`, {
        credentials: 'include'
      });
      const data = await response.json();
      if (!latestPOs.isLatest(seq)) return;   // superseded by a later search
      setPurchaseOrders(data.purchaseOrders || []);
      setPoTotalCount(data.total || 0);
    } catch (error) {
      if (!latestPOs.isLatest(seq)) return;
      console.error('Failed to fetch purchase orders:', error);
    }
  };

  const fetchBills = async () => {
    const seq = latestBills.begin();
    try {
      const params = new URLSearchParams({
        account_id: contactId,
        page: billsPage.toString(),
        limit: ITEMS_PER_PAGE.toString(),
        sortBy: billsSortField,
        sortDir: billsSortDirection,
        ...(billsSearch ? { search: billsSearch } : {}),
        ...(billsStatus ? { status: billsStatus } : {}),
        ...(billsDateFrom ? { dateFrom: billsDateFrom } : {}),
        ...(billsDateTo ? { dateTo: billsDateTo } : {})
      });
      const response = await fetch(`${API_URL}/api/bills/search?${params}`, { credentials: 'include' });
      const data = await response.json();
      if (!latestBills.isLatest(seq)) return;   // superseded by a later search
      setBills(data.bills || []);
      // totalCount, not total: `total` is nulled for a caller without
      // costs.view, because every amount on a bill is a cost.
      setBillsTotalCount(data.totalCount ?? data.total ?? data.pagination?.total ?? 0);
    } catch (error) {
      if (!latestBills.isLatest(seq)) return;
      console.error('Failed to fetch bills:', error);
    }
  };

  // By account id — never through the prescriptions text search, which would
  // match this account number inside any other one.
  const fetchPrescriptions = async () => {
    setPrescriptionsLoading(true);
    setPrescriptionsError(null);
    try {
      const res = await fetch(
        `${API_URL}/api/prescriptions/search?account_id=${contactId}&showArchived=true&limit=500`,
        { credentials: 'include' }
      );
      const data = await res.json();
      if (!res.ok || !data.success) throw new Error(data.error || 'Failed to load prescriptions');
      setPrescriptions(data.prescriptions || []);
    } catch (error) {
      console.error('Failed to fetch prescriptions:', error);
      setPrescriptions([]);
      setPrescriptionsError(error.message);
    } finally {
      setPrescriptionsLoading(false);
    }
  };

  const fetchComplaints = async () => {
    setComplaintsLoading(true);
    try {
      // Fire two requests — one as customer (account_id), one as supplier
      // (supplier_id) — and merge. Complaint rows have only one of the two
      // populated, so duplicates aren't possible.
      const [custRes, supRes] = await Promise.all([
        fetch(`${API_URL}/api/complaints/search?account_id=${contactId}&limit=500&showArchived=true`, { credentials: 'include' }),
        fetch(`${API_URL}/api/complaints/search?supplier_id=${contactId}&limit=500&showArchived=true`, { credentials: 'include' })
      ]);
      const cust = await custRes.json();
      const sup = await supRes.json();
      const list = [...(cust.complaints || cust.data || []), ...(sup.complaints || sup.data || [])];
      // Deduplicate by id (defensive)
      const seen = new Set();
      const merged = list.filter(c => (seen.has(c.id) ? false : (seen.add(c.id), true)));
      merged.sort((a, b) => new Date(b.complaint_date || b.created_at) - new Date(a.complaint_date || a.created_at));
      setComplaints(merged);
    } catch (error) {
      console.error('Failed to fetch complaints:', error);
    } finally {
      setComplaintsLoading(false);
    }
  };

  const toggleBillExpand = async (billId) => {
    const newExpanded = new Set(expandedBills);
    if (newExpanded.has(billId)) {
      newExpanded.delete(billId);
    } else {
      newExpanded.add(billId);
      if (!billLines[billId]) {
        setLoadingLines(prev => ({ ...prev, [`bill-${billId}`]: true }));
        try {
          const response = await fetch(`${API_URL}/api/bills/${billId}/lines`, { credentials: 'include' });
          const data = await response.json();
          setBillLines(prev => ({ ...prev, [billId]: data.lines || [] }));
        } catch (error) {
          console.error('Failed to fetch bill lines:', error);
        } finally {
          setLoadingLines(prev => ({ ...prev, [`bill-${billId}`]: false }));
        }
      }
    }
    setExpandedBills(newExpanded);
  };

  const handleExpandAllBills = async () => {
    if (expandedBills.size === bills.length) {
      setExpandedBills(new Set());
      return;
    }
    setExpandingAllBills(true);
    const idsToFetch = bills.filter(b => !billLines[b.id]).map(b => b.id);
    for (const billId of idsToFetch) {
      try {
        const response = await fetch(`${API_URL}/api/bills/${billId}/lines`, { credentials: 'include' });
        const data = await response.json();
        setBillLines(prev => ({ ...prev, [billId]: data.lines || [] }));
      } catch (error) {
        console.error('Failed to fetch bill lines:', error);
      }
    }
    setExpandedBills(new Set(bills.map(b => b.id)));
    setExpandingAllBills(false);
  };

  const handleBillSort = (field) => {
    if (billsSortField === field) {
      setBillsSortDirection(d => d === 'asc' ? 'desc' : 'asc');
    } else {
      setBillsSortField(field);
      setBillsSortDirection('desc');
    }
    setBillsPage(1);
  };

  const fetchCreditUsage = async () => {
    setCreditLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/credit-limits/customer/${contactId}`, {
        credentials: 'include'
      });
      const data = await response.json();
      if (data.success) {
        setCreditUsage(data.usage);
        setNewCreditLimit(data.usage.creditLimit || '');
      }
    } catch (error) {
      console.error('Failed to fetch credit usage:', error);
    } finally {
      setCreditLoading(false);
    }
  };

  const fetchPeriods = async () => {
    if (!contact) return;

    try {
      const [straightsRes, blendsRes, compoundsRes] = await Promise.all([
        fetch(`${API_URL}/api/pricing/monthly?limit=10000`, { credentials: 'include' }),
        fetch(`${API_URL}/api/pricing/contracts?category_id=2&limit=10000`, { credentials: 'include' }),
        fetch(`${API_URL}/api/pricing/contracts?category_id=3&limit=10000`, { credentials: 'include' })
      ]);

      const [straightsData, blendsData, compoundsData] = await Promise.all([
        straightsRes.json(),
        blendsRes.json(),
        compoundsRes.json()
      ]);

      const straightsContracts = (straightsData.data || []).map(mp => {
        const month = mp.month;
        let periodName;
        let period_start, period_end;

        if (month >= 10 || month <= 4) {
          const winterYear = month >= 10 ? mp.year : mp.year - 1;
          const winterEndYear = winterYear + 1;
          periodName = `Winter ${winterYear}-${winterEndYear.toString().slice(-2)}`;
          period_start = `${winterYear}-10-01`;
          period_end = `${winterEndYear}-04-30`;
        } else {
          periodName = `Summer ${mp.year}`;
          period_start = `${mp.year}-05-01`;
          period_end = `${mp.year}-09-30`;
        }

        return {
          ...mp,
          period_name: periodName,
          period_start,
          period_end,
          product_name: mp.item_name || mp.product_name,
          product_code: mp.item_code || mp.product_code
        };
      });

      const allContracts = [
        ...straightsContracts,
        ...(blendsData.data || []),
        ...(compoundsData.data || [])
      ];

      const customerContracts = allContracts.filter(c =>
        c.account_no === contact.account_no
      );

      // Periods that milk powder prices fall into (each month maps to a
      // Summer/Winter period) so a milk-powder-only customer still gets
      // selectable periods.
      let mpPeriods = [];
      try {
        const mpRes = await fetch(`${API_URL}/api/pricing/milk-powder/contract-prices/account/${contactId}`, { credentials: 'include' });
        const mpData = await mpRes.json();
        mpPeriods = (Array.isArray(mpData) ? mpData : []).flatMap(milkPowderSeasons);
      } catch (e) { /* milk powder periods are best-effort */ }

      const periodMap = new Map();
      customerContracts.forEach(contract => {
        if (contract.period_name && !periodMap.has(contract.period_name)) {
          periodMap.set(contract.period_name, {
            name: contract.period_name,
            start_date: contract.period_start,
            end_date: contract.period_end
          });
        }
      });
      mpPeriods.forEach(p => {
        if (!periodMap.has(p.name)) periodMap.set(p.name, p);
      });

      const uniquePeriods = Array.from(periodMap.values())
        .sort((a, b) => new Date(b.start_date) - new Date(a.start_date));

      setPeriods(uniquePeriods);

      if (uniquePeriods.length > 0) {
        let defaultPeriodName;

        const tempPeriodsWithIds = uniquePeriods.map((p, idx) => ({ ...p, id: idx }));
        const currentPeriodId = getCurrentPeriodId(tempPeriodsWithIds);
        if (currentPeriodId !== null) {
          const currentPeriod = tempPeriodsWithIds[currentPeriodId];
          if (currentPeriod) {
            defaultPeriodName = currentPeriod.name;
          }
        }

        if (!defaultPeriodName) {
          defaultPeriodName = uniquePeriods[0].name;
        }

        setSelectedPeriod(defaultPeriodName);
        fetchContractsForPeriod(defaultPeriodName, allContracts);
      } else {
        // No periods but may have spot pricing — trigger spot-only load
        setSelectedPeriod('__none__');
      }
    } catch (error) {
      console.error('Failed to fetch periods:', error);
    }
  };

  const fetchContractsForPeriod = (periodName, allContractsData) => {
    if (!contact || !periodName) return;

    const customerContracts = allContractsData.filter(c =>
      c.account_no === contact.account_no &&
      c.period_name === periodName
    );

    setContracts(customerContracts);
  };

  const toggleStraightsExpand = () => {
    setExpandedStraights(prev => !prev);
  };

  const getMonthsForPeriod = (periodName) => {
    if (!periodName) return [];

    const isWinter = periodName.startsWith('Winter');

    if (isWinter) {
      const years = periodName.match(/(\d{4})-(\d{2})/);
      if (!years) return [];
      const startYear = parseInt(years[1]);
      const endYear = parseInt('20' + years[2]);

      return [
        { month: 10, year: startYear, label: 'Oct' },
        { month: 11, year: startYear, label: 'Nov' },
        { month: 12, year: startYear, label: 'Dec' },
        { month: 1, year: endYear, label: 'Jan' },
        { month: 2, year: endYear, label: 'Feb' },
        { month: 3, year: endYear, label: 'Mar' },
        { month: 4, year: endYear, label: 'Apr' }
      ];
    } else {
      const year = parseInt(periodName.match(/\d{4}/)?.[0]);
      if (!year) return [];

      return [
        { month: 5, year, label: 'May' },
        { month: 6, year, label: 'Jun' },
        { month: 7, year, label: 'Jul' },
        { month: 8, year, label: 'Aug' },
        { month: 9, year, label: 'Sep' }
      ];
    }
  };

  const fetchContractPricing = async () => {
    if (!contactId || !contact) return;

    setContractsLoading(true);
    try {
      // Fetch spot pricing alongside contract pricing
      const spotRes = await fetch(`${API_URL}/api/pricing/contracts?category_id=5&limit=10000`, { credentials: 'include' });
      const spotData = await spotRes.json();

      // Spot prices filtered by account (not period)
      const customerSpotPrices = (spotData.data || []).filter(c =>
        c.account_no === contact.account_no
      );

      // If no period selected, only show spot prices
      if (!selectedPeriod || selectedPeriod === '__none__') {
        setContracts(customerSpotPrices);
        return;
      }

      const [straightsRes, blendsRes, compoundsRes] = await Promise.all([
        fetch(`${API_URL}/api/pricing/monthly?limit=10000`, { credentials: 'include' }),
        fetch(`${API_URL}/api/pricing/contracts?category_id=2&limit=10000`, { credentials: 'include' }),
        fetch(`${API_URL}/api/pricing/contracts?category_id=3&limit=10000`, { credentials: 'include' })
      ]);

      const [straightsData, blendsData, compoundsData] = await Promise.all([
        straightsRes.json(),
        blendsRes.json(),
        compoundsRes.json()
      ]);

      const straightsContracts = (straightsData.data || []).map(mp => {
        const month = mp.month;
        let periodName;

        if (month >= 10 || month <= 4) {
          const winterYear = month >= 10 ? mp.year : mp.year - 1;
          const winterEndYear = winterYear + 1;
          periodName = `Winter ${winterYear}-${winterEndYear.toString().slice(-2)}`;
        } else {
          periodName = `Summer ${mp.year}`;
        }

        return {
          ...mp,
          // Every monthly price is a straights price. The view takes the
          // category from the item, so a holding contract's months (no item
          // yet) would otherwise come back uncategorised.
          category: mp.category || 'straights',
          period_name: periodName,
          product_name: mp.item_name || mp.product_name,
          product_code: mp.item_code || mp.product_code
        };
      });

      const allContracts = [
        ...straightsContracts,
        ...(blendsData.data || []),
        ...(compoundsData.data || [])
      ];

      const customerContracts = allContracts.filter(c =>
        c.account_no === contact.account_no &&
        c.period_name === selectedPeriod
      );

      // Milk powder contract prices run From one day To another (no named
      // period). Each is listed under every Summer/Winter period its dates
      // touch, so they filter by the period selector, and shows its own dates.
      let milkPowderRows = [];
      try {
        const mpRes = await fetch(`${API_URL}/api/pricing/milk-powder/contract-prices/account/${contactId}`, { credentials: 'include' });
        const mpData = await mpRes.json();
        milkPowderRows = (Array.isArray(mpData) ? mpData : [])
          .filter(r => milkPowderSeasons(r).some(p => p.name === selectedPeriod))
          .map(r => {
            const price = r.sale_price != null ? parseFloat(r.sale_price) : null;
            const cost = r.cost_per_bag != null ? parseFloat(r.cost_per_bag) : null;
            return {
              category: 'milk powder',
              is_milk_powder: true,
              product_code: r.item_code,
              product_name: r.item_name,
              price,
              cost,
              margin: (price != null && cost != null) ? price - cost : null,
              period_name: selectedPeriod,
              mp_range_label: formatMilkRange(r),
              valid_from: r.valid_from,
              valid_to: r.valid_to,
              price_month: r.price_month
            };
          });
      } catch (e) {
        console.error('Failed to fetch milk powder contract pricing:', e);
      }

      setContracts([...customerContracts, ...milkPowderRows, ...customerSpotPrices]);
    } catch (error) {
      console.error('Failed to fetch contract pricing:', error);
    } finally {
      setContractsLoading(false);
    }
  };

  const handleUpdateCreditLimit = async () => {
    try {
      const response = await fetch(`${API_URL}/api/credit-limits/customer/${contactId}`, {
        method: 'PUT',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          creditLimit: parseFloat(newCreditLimit),
          notes: creditNotes
        })
      });
      const data = await response.json();
      if (data.success) {
        await fetchCreditUsage();
        setEditingCredit(false);
        setCreditNotes('');
      }
    } catch (error) {
      console.error('Failed to update credit limit:', error);
    }
  };

  // Invoice handlers
  const handleInvoiceSort = (field) => {
    if (invoiceSortField === field) {
      setInvoiceSortDirection(invoiceSortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setInvoiceSortField(field);
      setInvoiceSortDirection('desc');
    }
    setInvoicePage(1);
  };

  const toggleShowAllInvoices = () => {
    setShowAllInvoices(!showAllInvoices);
    setInvoicePage(1);
  };

  // PO handlers
  const handlePOSort = (field) => {
    if (poSortField === field) {
      setPoSortDirection(poSortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setPoSortField(field);
      setPoSortDirection('desc');
    }
    setPoPage(1);
  };

  const toggleShowAllPOs = () => {
    setShowAllPOs(!showAllPOs);
    setPoPage(1);
  };

  // Invoice expand/collapse handlers
  const toggleInvoiceExpand = async (invoiceId) => {
    const newExpanded = new Set(expandedInvoices);

    if (newExpanded.has(invoiceId)) {
      newExpanded.delete(invoiceId);
    } else {
      newExpanded.add(invoiceId);

      if (!invoiceLines[invoiceId]) {
        setLoadingLines(prev => ({ ...prev, [`inv-${invoiceId}`]: true }));
        try {
          // A lazy backfill of line items that were never synced, and a WRITE
          // despite being a GET — routes/invoices.js guards it with
          // denyReadOnly. Called unconditionally it hands a view-only account a
          // 403, which readOnlyFetch turns into "your account has view-only
          // access" for the crime of expanding an invoice to look at it.
          //
          // Skipped rather than caught: the request would be refused anyway, and
          // the /lines fetch below returns whatever is already stored, which for
          // any invoice that has ever been opened is all of it.
          if (!readOnly) {
            await fetch(
              `${API_URL}/api/invoices/${invoiceId}/ensure-line-items`,
              { credentials: 'include' }
            );
          }

          const response = await fetch(
            `${API_URL}/api/contacts/${contactId}/invoices/${invoiceId}/lines`,
            { credentials: 'include' }
          );
          const data = await response.json();
          setInvoiceLines(prev => ({ ...prev, [invoiceId]: data.lines }));
        } catch (error) {
          console.error('Failed to fetch invoice lines:', error);
        } finally {
          setLoadingLines(prev => ({ ...prev, [`inv-${invoiceId}`]: false }));
        }
      }
    }

    setExpandedInvoices(newExpanded);
  };

  // PO expand/collapse handlers
  const togglePOExpand = async (poId) => {
    const newExpanded = new Set(expandedPOs);

    if (newExpanded.has(poId)) {
      newExpanded.delete(poId);
    } else {
      newExpanded.add(poId);

      if (!poLines[poId]) {
        setLoadingLines(prev => ({ ...prev, [`po-${poId}`]: true }));
        try {
          const response = await fetch(
            `${API_URL}/api/contacts/${contactId}/purchase-orders/${poId}/lines`,
            { credentials: 'include' }
          );
          const data = await response.json();
          setPoLines(prev => ({ ...prev, [poId]: data.lines }));
        } catch (error) {
          console.error('Failed to fetch PO lines:', error);
        } finally {
          setLoadingLines(prev => ({ ...prev, [`po-${poId}`]: false }));
        }
      }
    }

    setExpandedPOs(newExpanded);
  };

  // Expand/Collapse All Invoices
  const handleExpandAllInvoices = async () => {
    if (expandedInvoices.size === invoices.length) {
      setExpandedInvoices(new Set());
      return;
    }

    setExpandingAllInvoices(true);
    const allIds = new Set(invoices.map(inv => inv.id));

    const idsToFetch = invoices.filter(inv => !invoiceLines[inv.id]).map(inv => inv.id);

    if (idsToFetch.length > 0) {
      const loadingState = {};
      idsToFetch.forEach(id => { loadingState[`inv-${id}`] = true; });
      setLoadingLines(prev => ({ ...prev, ...loadingState }));

      await Promise.all(idsToFetch.map(async (invoiceId) => {
        try {
          await fetch(
            `${API_URL}/api/invoices/${invoiceId}/ensure-line-items`,
            { credentials: 'include' }
          );

          const response = await fetch(
            `${API_URL}/api/contacts/${contactId}/invoices/${invoiceId}/lines`,
            { credentials: 'include' }
          );
          const data = await response.json();
          setInvoiceLines(prev => ({ ...prev, [invoiceId]: data.lines }));
        } catch (error) {
          console.error('Failed to fetch invoice lines:', error);
        } finally {
          setLoadingLines(prev => ({ ...prev, [`inv-${invoiceId}`]: false }));
        }
      }));
    }

    setExpandedInvoices(allIds);
    setExpandingAllInvoices(false);
  };

  // Expand/Collapse All POs
  const handleExpandAllPOs = async () => {
    if (expandedPOs.size === purchaseOrders.length) {
      setExpandedPOs(new Set());
      return;
    }

    setExpandingAllPOs(true);
    const allIds = new Set(purchaseOrders.map(po => po.id));

    const idsToFetch = purchaseOrders.filter(po => !poLines[po.id]).map(po => po.id);

    if (idsToFetch.length > 0) {
      const loadingState = {};
      idsToFetch.forEach(id => { loadingState[`po-${id}`] = true; });
      setLoadingLines(prev => ({ ...prev, ...loadingState }));

      await Promise.all(idsToFetch.map(async (poId) => {
        try {
          const response = await fetch(
            `${API_URL}/api/contacts/${contactId}/purchase-orders/${poId}/lines`,
            { credentials: 'include' }
          );
          const data = await response.json();
          setPoLines(prev => ({ ...prev, [poId]: data.lines }));
        } catch (error) {
          console.error('Failed to fetch PO lines:', error);
        } finally {
          setLoadingLines(prev => ({ ...prev, [`po-${poId}`]: false }));
        }
      }));
    }

    setExpandedPOs(allIds);
    setExpandingAllPOs(false);
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: 'GBP',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount || 0);
  };

  // Format address from flat contact fields using a prefix
  const formatAddressFromFlat = (prefix) => {
    const str = formatAddressFromFlatUtil(contact, prefix, { separator: '\n' });
    return str || null;
  };

  // A tel: href and the number as shown, from country/area code + number —
  // utils/phoneNumber (2.19.2: moved there so the rep phone's customer card
  // dials the same number this window shows).
  const buildTelHref = phoneTelHref;
  const formatPhoneDisplay = phoneDisplay;

  const getUtilizationColor = (percentage) => {
    if (percentage >= 100) return 'var(--color-error)';
    if (percentage >= 90) return 'var(--color-warning)';
    if (percentage >= 75) return 'var(--color-info)';
    return 'var(--color-success)';
  };

  const handleClose = () => {
    setClosing(true);
    setTimeout(() => {
      document.body.classList.remove('modal-open');
      const contentWrapper = document.querySelector('.content-wrapper');
      if (contentWrapper) {
        contentWrapper.classList.remove('modal-open');
      }
      onClose();
    }, 300);
  };

  const handlePanelToggle = (panel) => {
    if (activePanel === panel) {
      setActivePanel(null);
    } else {
      setActivePanel(panel);
    }
  };

  if (!contactId) return null;

  // Read-only chips for the linked scopes on a joint-account row.
  const LINK_SCOPES = [
    ['link_statements', 'Statements'],
    ['link_reports', 'Reports'],
    ['link_pricing', 'Pricing'],
    ['link_credit', 'Credit']
  ];
  const renderScopeChips = (o) => {
    const on = LINK_SCOPES.filter(([k]) => o && o[k]);
    if (on.length === 0) return <span className="no-data" style={{ fontSize: '0.85em' }}>No shared scopes</span>;
    return (
      <span className="scope-chips">
        {on.map(([k, label]) => <span key={k} className="scope-chip">{label}</span>)}
      </span>
    );
  };

  return createPortal(
    <>
    <div className={`modal-overlay ${closing ? 'closing' : ''}`} onClick={handleClose}>
      <div
        className={`modal-content contact-modal ${closing ? 'closing' : ''} ${activePanel ? 'expanded' : ''}${repMobile && activePanel ? ' rep-tab-open' : ''}`}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Left Panel - Always Visible */}
        <div className="contact-panel-left">
          <div className="panel-header">
            <div className="header-title">
              {!loading && contact && (
                <span
                  className={`status-icon ${contact.contact_status === 'ARCHIVED' ? 'status-archived' : 'status-active'}`}
                  title={contact.contact_status || 'ACTIVE'}
                >
                  <span className="material-symbols-rounded">
                    {contact.contact_status === 'ARCHIVED' ? 'archive' : 'check_circle'}
                  </span>
                </span>
              )}
              {contact?.account_no && (
                <span className='account-no'>{contact?.account_no}</span>
              )}
              <h2>{loading ? 'Loading...' : contact?.company_name}</h2>
              {contact && (
                <div className="groups-inline">
                  <span className="group-tag">{(contact.contact_type || 'Unknown').toLowerCase().replace(/^\w/, c => c.toUpperCase())}</span>
                </div>
              )}
            </div>
            <div className="header-actions">
              {!loading && contact && <FindInSearchButton type="contact" record={contact} onClose={handleClose} variant="icon" />}
              {/* A view-only account gets the notes editor instead of the full
                  one — the single field they are allowed to write, behind a
                  button that says so. Offering them "Edit" and refusing the
                  save is the failure the read-only design exists to avoid.

                  Not on a phone: the contact modal there is already cut down to
                  one panel and the header has no room, and this is a desk job. */}
              {!loading && contact && readOnly && !isPhone && (
                <button
                  className="btn-icon"
                  onClick={() => setShowNotesEdit(true)}
                  title="Edit customer notes"
                >
                  <span className="material-symbols-rounded">edit_note</span>
                </button>
              )}
              {!loading && contact && !readOnly && (
                <button
                  className="btn-icon"
                  onClick={() => {
                    if (onEdit) {
                      // Parent screen owns the edit flow (e.g. Search Contacts).
                      handleClose();
                      onEdit(contact);
                    } else {
                      // No parent handler — open our own edit modal in place.
                      setShowInternalEdit(true);
                    }
                  }}
                  title="Edit contact"
                >
                  <span className="material-symbols-rounded">edit</span>
                </button>
              )}
              <button
                className="btn-icon"
                onClick={() => setShowHistory(true)}
                disabled={loading}
                title="View change history"
              >
                <span className="material-symbols-rounded">history</span>
              </button>
              <button className="btn-icon close-btn" onClick={handleClose}>
                <span className="material-symbols-rounded">close</span>
              </button>
            </div>
          </div>

          {/* A rep's phone: the tabs, and the tab's content in place of the
              details (RepContactTabs; RepMobile.css hides .panel-body while
              a tab other than Details is open). */}
          {repMobile && !loading && contact && (
            <RepContactTabBar
              active={['contracts', 'rep-orders', 'invoices'].includes(activePanel) ? activePanel : null}
              onChange={(key) => setActivePanel(key)}
              counts={{ contracts: contracts.length, invoices: invoiceTotalCount }}
            />
          )}
          {repMobile && !loading && contact && activePanel === 'contracts' && (
            <RepContactContracts
              contracts={contracts}
              loading={contractsLoading}
              periods={periods}
              selectedPeriod={selectedPeriod}
              onPeriodChange={setSelectedPeriod}
              onOpenAllContracts={access.can('nav.rep-contracts') && typeof onNavigate === 'function'
                ? () => {
                  handleClose();
                  // My contracts reads its filters from the nav store when it
                  // mounts (usePersistedState) — navigateToMenu passes no
                  // params on — so land it narrowed to this customer, page 1.
                  const nav = useNavStore.getState();
                  nav.setPage('repContracts.filters', { ...defaultRepContractFilters(null), accountId: String(contact.id) });
                  nav.setPage('repContracts.page', 1);
                  onNavigate('rep-contracts', { accountId: contact.id });
                }
                : null}
              openAllBlock={access.can('nav.rep-contracts')
                ? 'Open My Contracts from the menu'
                : 'My Contracts is not open to you'}
            />
          )}
          {repMobile && !loading && contact && activePanel === 'rep-orders' && (
            <RepContactOrders accountId={contact.id} onOpenOrder={(id) => openEntity('customer_order', id)} />
          )}
          {repMobile && !loading && contact && activePanel === 'invoices' && (
            <RepContactInvoices
              invoices={invoices}
              onOpenInvoice={(id) => openEntity('invoice', id)}
              pagination={{
                page: invoicePage,
                limit: ITEMS_PER_PAGE,
                total: invoiceTotalCount,
                totalPages: Math.ceil(invoiceTotalCount / ITEMS_PER_PAGE)
              }}
              onPageChange={setInvoicePage}
            />
          )}

          {!loading && contact && (
            <div className="panel-body">
              {/* Account Information - Three Columns */}
              <div className="info-section">
                <h3>Account Information</h3>
                <div className="info-columns">
                  {/* Order tiles a 3-column grid cleanly:
                      row 1: Email (span 2) · Phone
                      row 2: Groups (span 2) · Mobile
                      row 3: Sales Rep · Contracted · VAT
                      Credit (conditional) trails on its own row. */}
                  <div className="info-item span-2">
                    <span className="info-label">Email</span>
                    <span className="info-value">
                      {contact.email
                        ? <a href={`mailto:${contact.email}`}>{contact.email}</a>
                        : 'Not provided'}
                    </span>
                  </div>

                  <div className="info-item">
                    <span className="info-label">Phone</span>
                    <span className="info-value">
                      {contact.phone
                        ? <a href={buildTelHref(contact.phone, contact.phone_country_code, contact.phone_area_code)}>
                            {formatPhoneDisplay(contact.phone, contact.phone_country_code, contact.phone_area_code)}
                          </a>
                        : formatPhoneDisplay(contact.phone, contact.phone_country_code, contact.phone_area_code)}
                    </span>
                  </div>

                  {/* Groups — spans 2 columns */}
                  <div className="info-item info-groups-span">
                    <span className="info-label">Groups</span>
                    <div className="groups-inline">
                      {contact.groups ? (
                        contact.groups.split(',').map((group, idx) => (
                          <span key={idx} className="group-tag">{group.trim()}</span>
                        ))
                      ) : (
                        <span className="no-data">None</span>
                      )}
                    </div>
                  </div>

                  <div className="info-item">
                    <span className="info-label">Mobile</span>
                    <span className="info-value">
                      {contact.mobile
                        ? <a href={buildTelHref(contact.mobile, contact.mobile_country_code, contact.mobile_area_code)}>
                            {formatPhoneDisplay(contact.mobile, contact.mobile_country_code, contact.mobile_area_code)}
                          </a>
                        : formatPhoneDisplay(contact.mobile, contact.mobile_country_code, contact.mobile_area_code)}
                    </span>
                  </div>

                  <div className="info-item">
                    <span className="info-label">Sales Rep</span>
                    <span className="info-value">{contact.sales_rep || 'Not assigned'}</span>
                  </div>

                  <div className="info-item">
                    <span className="info-label">Contracted</span>
                    <span className="info-value" style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                      <span className="material-symbols-rounded" style={{ fontSize: '16px', color: contact.contracted ? 'var(--color-primary)' : 'var(--text-muted)' }}>
                        {contact.contracted ? 'check_circle' : 'cancel'}
                      </span>
                      {contact.contracted ? 'Yes' : 'No'}
                    </span>
                  </div>

                  {/* Last slot: VAT Number if one is stored; otherwise the Credit
                      Limit status if the account is credit-controlled; otherwise
                      the slot is left empty. */}
                  {contact.tax_number ? (
                    <div className="info-item">
                      <span className="info-label">VAT Number</span>
                      <span className="info-value">{contact.tax_number}</span>
                    </div>
                  ) : contact.credit_status ? (
                    <div className="info-item">
                      <span className="info-label">Credit Limit</span>
                      <span
                        className="info-value"
                        style={{
                          color: contact.credit_status === 'over' ? 'var(--color-error)' : 'var(--color-success)',
                          fontWeight: 600
                        }}
                      >
                        {contact.credit_status === 'over' ? 'Over Limit' : 'Under Limit'}
                      </span>
                    </div>
                  ) : null}
                </div>
              </div>

              {/* Addresses - Two Columns */}
              <div className="info-section">
                <h3>Addresses</h3>
                <div className="address-columns">
                  <div className="address-col">
                    <span className="address-type">Delivery</span>
                    <span className="address-value">
                      {formatAddressFromFlat('delivery_') || 'Not set'}
                    </span>
                  </div>
                  <div className="address-col">
                    <span className="address-type">Invoice</span>
                    <span className="address-value">
                      {formatAddressFromFlat('invoice_') || 'Not set'}
                    </span>
                  </div>
                </div>
              </div>

              {/* Contact Persons */}
              {contact.contactPersons && contact.contactPersons.length > 0 && (
                <div className="info-section">
                  <h3>Contact Persons</h3>
                  <p className="contact-persons-hint">
                    Who receives statement emails, and who receives purchase orders. Edit the contact to change.
                  </p>
                  <div className="contact-persons-list">
                    {contact.contactPersons.map((person, idx) => (
                      <div key={idx} className="contact-person-item">
                        {/* Star beside the name it belongs to, rather than
                            floating at the end of the row where it read as a
                            property of the whole line. */}
                        <span className="person-name">
                          {[person.first_name, person.last_name].filter(Boolean).join(' ') || 'Unnamed'}
                          {person.is_primary && (
                            <span className="person-badge primary" title="Primary contact">
                              <span className="material-symbols-rounded">star</span>
                            </span>
                          )}
                        </span>
                        <span className="person-email">
                          {person.email && <a href={`mailto:${person.email}`}>{person.email}</a>}
                        </span>
                        <span className="person-spare" />
                        {/* Read-only here — editable in the contact editor. */}
                        <span className="person-email-flag" title={
                          person.is_primary
                            ? 'Primary email — always included in Xero emails'
                            : (person.include_in_emails ? 'Included in statement emails' : 'Not included in statement emails')
                        }>
                          <span className="flag-caption">Statements</span>
                          {person.email && (
                            <TickCheckbox
                              id={`email-flag-${contactId}-${idx}`}
                              checked={person.is_primary ? true : !!person.include_in_emails}
                              disabled
                              className={person.is_primary ? 'primary-locked' : ''}
                              ariaLabel={`${person.email} ${person.include_in_emails || person.is_primary ? 'included in' : 'excluded from'} statement emails`}
                            />
                          )}
                        </span>
                        {/* Who at a SUPPLIER receives purchase orders. A
                            different question from statements, which is why it
                            is a different tick — and a question that does not
                            arise at all on a customer-only contact, who will
                            never be sent one. */}
                        {['SUPPLIER', 'BOTH'].includes(contact?.contact_type) && (
                        <span className="person-email-flag" title={
                          person.include_in_po_emails
                            ? 'Included in purchase order emails to this supplier'
                            : 'Not included in purchase order emails'
                        }>
                          <span className="flag-caption">POs</span>
                          {person.email && (
                            <TickCheckbox
                              id={`po-flag-${contactId}-${idx}`}
                              checked={!!person.include_in_po_emails}
                              disabled
                              ariaLabel={`${person.email} ${person.include_in_po_emails ? 'included in' : 'excluded from'} purchase order emails`}
                            />
                          )}
                        </span>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Account Linking (joint accounts) — read-only; managed in the
                  Joint Accounts screen. Shows the parent this account rolls up to
                  (with its own shared scopes) and/or the child accounts linked to it. */}
              {(contact.parentAccount || (contact.childAccounts && contact.childAccounts.length > 0)) && (
                <div className="info-section account-linking">
                  <h3>Account Linking <span className="section-note">Managed in Joint Accounts</span></h3>

                  {contact.parentAccount && (
                    <div className="linking-block">
                      <span className="linking-label">Rolls up to</span>
                      <div className="linked-account-row">
                        <span className="linked-account">
                          {contact.parentAccount.account_no && <span className="product-code">{contact.parentAccount.account_no}</span>}
                          {contact.parentAccount.company_name}
                        </span>
                        {renderScopeChips(contact)}
                      </div>
                    </div>
                  )}

                  {contact.childAccounts && contact.childAccounts.length > 0 && (
                    <div className="linking-block">
                      <span className="linking-label">
                        {contact.childAccounts.length} linked account{contact.childAccounts.length !== 1 ? 's' : ''}
                      </span>
                      <div className="linked-children-list">
                        {contact.childAccounts.map(ch => (
                          <div key={ch.id} className="linked-account-row">
                            <span className="linked-account">
                              {ch.account_no && <span className="product-code">{ch.account_no}</span>}
                              {ch.company_name}
                            </span>
                            {renderScopeChips(ch)}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Notes, and what will get down the lane. Both internal: they
                  never reach a customer or a supplier. */}
              <div className="info-section">
                <h3>Notes</h3>
                <div className="notes-display">
                  {contact.notes || <span className="no-data">No notes</span>}
                </div>
                <div className="lva-display" title="Largest vehicle that can reach this customer — internal only">
                  <span className="lva-label">Largest Vehicle Access</span>
                  <span className="lva-value">
                    {contact.largest_vehicle_access || <span className="no-data">Not recorded</span>}
                  </span>
                </div>
              </div>
            </div>
          )}

          {/* Panel Navigation Rail — round icon buttons down the right edge */}
          <div className="panel-nav-buttons">
            <button
              className={`contact-nav-btn ${activePanel === 'invoices' ? 'active' : ''}`}
              onClick={() => handlePanelToggle('invoices')}
              data-tooltip="Invoices"
            >
              <span className="material-symbols-rounded">receipt</span>
              {invoiceTotalCount > 0 && <span className="nav-btn-badge">{invoiceTotalCount}</span>}
            </button>
            <button
              className={`contact-nav-btn ${activePanel === 'purchase-orders' ? 'active' : ''}`}
              onClick={() => handlePanelToggle('purchase-orders')}
              data-tooltip="Orders"
            >
              <span className="material-symbols-rounded">shopping_cart</span>
              {poTotalCount > 0 && <span className="nav-btn-badge">{poTotalCount}</span>}
            </button>
            <button
              className={`contact-nav-btn ${activePanel === 'contracts' ? 'active' : ''}`}
              onClick={() => handlePanelToggle('contracts')}
              data-tooltip="Contracts"
            >
              <span className="material-symbols-rounded">handshake</span>
              {contracts.length > 0 && <span className="nav-btn-badge">{contracts.length}</span>}
            </button>
            {/* The badge counts prescriptions in use, not every one on file: most
                customers have a run of expired ones, and "23" would say nothing
                about whether a medicated feed can be ordered today. */}
            <button
              className={`contact-nav-btn ${activePanel === 'prescriptions' ? 'active' : ''}`}
              onClick={() => handlePanelToggle('prescriptions')}
              disabled={contact?.contact_type === 'SUPPLIER'}
              data-tooltip="Prescriptions"
            >
              <span className="material-symbols-rounded">medication</span>
              {prescriptionsInUse > 0 && <span className="nav-btn-badge">{prescriptionsInUse}</span>}
            </button>
            {/* The customer's blend formulations by product type, and a new one
                made from here — item, formulation and placement in one go.
                Offered to those who can see formulations at all. */}
            {access.can('nav.formulations') && (
              <button
                className={`contact-nav-btn ${activePanel === 'formulations' ? 'active' : ''}`}
                onClick={() => handlePanelToggle('formulations')}
                disabled={contact?.contact_type === 'SUPPLIER'}
                data-tooltip="Formulations"
              >
                <span className="material-symbols-rounded">science</span>
              </button>
            )}
            {contact && 'credit_limit' in contact && (
              <button
                className={`contact-nav-btn ${activePanel === 'credit' ? 'active' : ''}`}
                onClick={() => handlePanelToggle('credit')}
                data-tooltip="Credit"
              >
                <span className="material-symbols-rounded">account_balance</span>
              </button>
            )}
            <button
              className={`contact-nav-btn ${activePanel === 'bills' ? 'active' : ''}`}
              onClick={() => handlePanelToggle('bills')}
              disabled={contact?.contact_type === 'CUSTOMER'}
              data-tooltip="Bills"
            >
              <span className="material-symbols-rounded">receipt_long</span>
              {billsTotalCount > 0 && <span className="nav-btn-badge">{billsTotalCount}</span>}
            </button>
            {/* What this supplier charges to deliver direct. A purchase cost,
                so only offered to those who can see costs. */}
            {access.can('costs.view') && (
              <button
                className={`contact-nav-btn ${activePanel === 'haulage-rates' ? 'active' : ''}`}
                onClick={() => handlePanelToggle('haulage-rates')}
                disabled={!['SUPPLIER', 'BOTH'].includes(contact?.contact_type)}
                data-tooltip="Haulage rates"
              >
                <span className="material-symbols-rounded">local_shipping</span>
              </button>
            )}
            <button
              className={`contact-nav-btn ${activePanel === 'statements' ? 'active' : ''}`}
              onClick={() => handlePanelToggle('statements')}
              disabled={contact?.contact_type === 'SUPPLIER'}
              data-tooltip="Statements"
            >
              <span className="material-symbols-rounded">format_list_bulleted</span>
            </button>
            <button
              className={`contact-nav-btn ${activePanel === 'complaints' ? 'active' : ''}`}
              onClick={() => handlePanelToggle('complaints')}
              data-tooltip="Complaints"
            >
              <span className="material-symbols-rounded">report_problem</span>
              {complaints.length > 0 && <span className="nav-btn-badge">{complaints.length}</span>}
            </button>
          </div>
        </div>

        {/* Right Panel - Slides Out */}
        <div className={`contact-panel-right ${activePanel ? 'open' : ''}`}>
          <div className="right-panel-header">
            <h3>
              {activePanel === 'invoices' && 'Invoices'}
              {activePanel === 'purchase-orders' && 'Purchase Orders'}
              {activePanel === 'contracts' && 'Contracts'}
              {activePanel === 'prescriptions' && 'Prescriptions'}
              {activePanel === 'formulations' && 'Formulations'}
              {activePanel === 'credit' && 'Credit & Payments'}
              {activePanel === 'bills' && 'Bills'}
              {activePanel === 'haulage-rates' && 'Haulage Rates'}
              {activePanel === 'statements' && 'Statements'}
              {activePanel === 'complaints' && 'Complaints'}
            </h3>
            {/* The panel is a window on a list; this is the whole list, for
                this contact only. Shown only where the panel HAS a screen and
                this user is allowed on it — a link to somewhere their nav does
                not offer would land them on a page they cannot open. */}
            {(() => {
              const target = PANEL_SEARCH_TARGETS[activePanel];
              if (!target || typeof onNavigate !== 'function') return null;
              if (!access.can(`nav.${target.menu}`)) return null;
              const term = (contact?.account_no || contact?.company_name || '').trim();
              if (!term) return null;
              return (
                <button
                  className="btn-icon panel-open-in-search"
                  title={`Open ${target.label} filtered to ${term}`}
                  onClick={() => revealInSearch(onNavigate, target.menu, term, onClose, { tab: target.tab })}
                >
                  <span className="material-symbols-rounded">open_in_new</span>
                </button>
              );
            })()}
            <button className="btn-icon" onClick={() => setActivePanel(null)} title="Collapse panel">
              <span className="material-symbols-rounded">chevron_left</span>
            </button>
          </div>

          <div className="right-panel-content">
            {/* Invoices Panel */}
            {activePanel === 'invoices' && (
              <div key="invoices" className={`panel-content-section ${isPanelOpening ? 'panel-opening' : ''}`}>
                <div className="panel-filters">
                  <div className="panel-search">
                    <Input type="text" placeholder="Search #, ref, item code, product..." value={invoiceSearch} onChange={(e) => { setInvoiceSearch(e.target.value); setInvoicePage(1); }} onEnter={() => fetchContactInvoices()} icon="search" />
                    <SearchHelp amounts fields={['invoice no.', 'reference', 'item code', 'product', 'total']} />
                  </div>
                  <Dropdown
                    value={invoiceStatus}
                    onChange={(value) => { setInvoiceStatus(value); setInvoicePage(1); }}
                    options={[
                      { value: '', label: 'All Active' },
                      { value: 'DRAFT', label: 'Draft' },
                      { value: 'SUBMITTED', label: 'Submitted' },
                      { value: 'AUTHORISED', label: 'Authorised' },
                      { value: 'PAID', label: 'Paid' },
                      { value: 'VOIDED', label: 'Voided' }
                    ]}
                  />
                  <DateInput value={invoiceDateFrom} onChange={(e) => { setInvoiceDateFrom(e.target.value); setInvoicePage(1); }} placeholder="From" />
                  <DateInput value={invoiceDateTo} onChange={(e) => { setInvoiceDateTo(e.target.value); setInvoicePage(1); }} placeholder="To" />
                </div>

                <div className="panel-toolbar">
                  <div className="toolbar-stats">
                    {invoiceTotalCount} invoice{invoiceTotalCount !== 1 ? 's' : ''}
                  </div>
                  <div className="toolbar-actions">
                    <button
                      className="btn-secondary btn-sm"
                      onClick={handleExpandAllInvoices}
                      disabled={expandingAllInvoices || invoices.length === 0}
                    >
                      <span className="material-symbols-rounded">
                        {expandedInvoices.size === invoices.length && invoices.length > 0 ? 'unfold_less' : 'unfold_more'}
                      </span>
                      {expandingAllInvoices ? 'Loading...' : (expandedInvoices.size === invoices.length && invoices.length > 0 ? 'Collapse All' : 'Expand All')}
                    </button>
                  </div>
                </div>

                <div className="table-wrapper">
                  <table className="data-table">
                    <thead>
                      <tr>
                        <th style={{ width: '30px' }}></th>
                        <th className="sortable" onClick={() => handleInvoiceSort('date')}>
                          Date {invoiceSortField === 'date' && (invoiceSortDirection === 'asc' ? '▲' : '▼')}
                        </th>
                        <th>Invoice #</th>
                        <th>Reference</th>
                        <th className="text-right sortable" onClick={() => handleInvoiceSort('amount')}>
                          Total {invoiceSortField === 'amount' && (invoiceSortDirection === 'asc' ? '▲' : '▼')}
                        </th>
                        <th className="text-right">Paid</th>
                        <th className="text-right">Balance</th>
                        <th style={{ textAlign: "center" }}>Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {invoices.length > 0 ? (
                        invoices.map(inv => (
                          <React.Fragment key={inv.id}>
                            <tr className="expandable-row" onClick={() => toggleInvoiceExpand(inv.id)}>
                              <td className="expand-cell">
                                <span className={`expand-icon ${expandedInvoices.has(inv.id) ? 'expanded' : ''}`}>
                                  ▼
                                </span>
                              </td>
                              <td>{formatDateShort(inv.invoice_date)}</td>
                              <td>
                                <span
                                  className="entity-number-link"
                                  onClick={(e) => { e.stopPropagation(); openEntity('invoice', inv.id); }}
                                  title={`Open ${inv.invoice_no}`}
                                >
                                  {inv.invoice_no}
                                </span>
                              </td>
                              <td>{inv.reference || '-'}</td>
                              <td className="text-right">{formatCurrency(inv.total)}</td>
                              <td className="text-right">{formatCurrency(inv.amount_paid)}</td>
                              <td className="text-right" style={{ fontWeight: '600' }}>
                                {formatCurrency(inv.total - inv.amount_paid)}
                              </td>
                              <td className="status-col">
                                <span className={`status-badge status-${inv.status?.toLowerCase()}`}>
                                  {inv.status}
                                </span>
                              </td>
                            </tr>
                            <tr className={`expanded-content ${expandedInvoices.has(inv.id) ? 'expanded' : ''}`}>
                              <td colSpan="8">
                                <div className="line-items-container">
                                  {loadingLines[`inv-${inv.id}`] ? (
                                    <div className="loading-lines">Loading line items...</div>
                                  ) : invoiceLines[inv.id]?.length > 0 ? (
                                    <table className="line-items-table">
                                      <thead>
                                        <tr>
                                          <th>Description</th>
                                          <th>Item #</th>
                                          <th className="text-right">Qty</th>
                                          <th className="text-right">Unit</th>
                                          <th className="text-right">Line</th>
                                          <th>Tax %</th>
                                        </tr>
                                      </thead>
                                      <tbody>
                                        {invoiceLines[inv.id].map((line, idx) => (
                                          <tr key={idx}>
                                            <td>{line.description}</td>
                                            <td style={{ color: 'var(--text-muted)' }}>{line.item_code || '-'}</td>
                                            <td className="text-right">{formatQuantity(line.quantity)}</td>
                                            <td className="text-right">{formatPrice(line.unit_price)}</td>
                                            <td className="text-right">{formatCurrency(line.line_total)}</td>
                                            <td>{getTaxPercentDisplay(line.tax_type)}</td>
                                          </tr>
                                        ))}
                                      </tbody>
                                    </table>
                                  ) : expandedInvoices.has(inv.id) && !loadingLines[`inv-${inv.id}`] ? (
                                    <div className="no-lines">No line items found</div>
                                  ) : null}
                                </div>
                              </td>
                            </tr>
                          </React.Fragment>
                        ))
                      ) : (
                        <tr>
                          <td colSpan="8" className="text-center">
                            No invoices found
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>

                {invoiceTotalCount > ITEMS_PER_PAGE && (
                  <PaginationControls
                    pagination={{
                      page: invoicePage,
                      limit: ITEMS_PER_PAGE,
                      total: invoiceTotalCount,
                      totalPages: Math.ceil(invoiceTotalCount / ITEMS_PER_PAGE)
                    }}
                    onPageChange={setInvoicePage}
                  />
                )}
              </div>
            )}

            {/* Purchase Orders Panel */}
            {activePanel === 'purchase-orders' && (
              <div key="purchase-orders" className={`panel-content-section ${isPanelOpening ? 'panel-opening' : ''}`}>
                
                <div className="panel-filters">
                  <div className="panel-search">
                    <Input type="text" placeholder="Search PO #, ref, product..." value={poSearch} onChange={(e) => { setPoSearch(e.target.value); setPoPage(1); }} onEnter={() => fetchPurchaseOrders()} icon="search" />
                    <SearchHelp amounts fields={['PO no.', 'reference', 'supplier ref.', 'product', 'item code', 'total']} />
                  </div>
                  <Dropdown
                    value={poStatus}
                    onChange={(value) => { setPoStatus(value); setPoPage(1); }}
                    options={[
                      { value: '', label: 'All Active' },
                      { value: 'DRAFT', label: 'Draft' },
                      { value: 'SENT', label: 'Sent' },
                      { value: 'AUTHORISED', label: 'Authorised' },
                      { value: 'BILLED', label: 'Billed' },
                      { value: 'CONFIRMED', label: 'Confirmed' },
                      { value: 'DELETED', label: 'Deleted' }
                    ]}
                  />
                  <DateInput value={poDateFrom} onChange={(e) => { setPoDateFrom(e.target.value); setPoPage(1); }} placeholder="From" />
                  <DateInput value={poDateTo} onChange={(e) => { setPoDateTo(e.target.value); setPoPage(1); }} placeholder="To" />
                </div>

                <div className="panel-toolbar">
                  <div className="toolbar-stats">
                    {poTotalCount} purchase order{poTotalCount !== 1 ? 's' : ''}
                  </div>
                  <div className="toolbar-actions">
                    <button
                      className="btn-secondary btn-sm"
                      onClick={handleExpandAllPOs}
                      disabled={expandingAllPOs || purchaseOrders.length === 0}
                    >
                      <span className="material-symbols-rounded">
                        {expandedPOs.size === purchaseOrders.length && purchaseOrders.length > 0 ? 'unfold_less' : 'unfold_more'}
                      </span>
                      {expandingAllPOs ? 'Loading...' : (expandedPOs.size === purchaseOrders.length && purchaseOrders.length > 0 ? 'Collapse All' : 'Expand All')}
                    </button>
                  </div>
                </div>

                <div className="table-wrapper">
                  <table className="data-table">
                    <thead>
                      <tr>
                        <th style={{ width: '30px' }}></th>
                        <th className="sortable" onClick={() => handlePOSort('date')}>
                          Date {poSortField === 'date' && (poSortDirection === 'asc' ? '▲' : '▼')}
                        </th>
                        <th>PO Number</th>
                        <th>Reference</th>
                        <th className="text-right sortable" onClick={() => handlePOSort('amount')}>
                          Total {poSortField === 'amount' && (poSortDirection === 'asc' ? '▲' : '▼')}
                        </th>
                        <th style={{ textAlign: "center" }}>Status</th>
                        <th>Delivery</th>
                      </tr>
                    </thead>
                    <tbody>
                      {purchaseOrders.length > 0 ? (
                        purchaseOrders.map(po => (
                          <React.Fragment key={po.id}>
                            <tr className="expandable-row" onClick={() => togglePOExpand(po.id)}>
                              <td className="expand-cell">
                                <span className={`expand-icon ${expandedPOs.has(po.id) ? 'expanded' : ''}`}>
                                  ▼
                                </span>
                              </td>
                              <td>{formatDateShort(po.date)}</td>
                              <td>
                                {/* The XERO id, not po.id — PurchaseOrderModal
                                    queries WHERE xero_purchase_order_id = $1 and
                                    a local integer returns "not found" with no
                                    error at all. See CLAUDE.md. */}
                                <span
                                  className="entity-number-link"
                                  onClick={(e) => { e.stopPropagation(); openEntity('purchase_order', po.xero_purchase_order_id); }}
                                  title={`Open ${po.purchase_order_number}`}
                                >
                                  {po.purchase_order_number}
                                </span>
                              </td>
                              <td>{po.reference || '-'}</td>
                              <td className="text-right">{formatCurrency(po.total)}</td>
                              <td className="status-col">
                                <span className={`status-badge status-${po.status?.toLowerCase()}`}>
                                  {po.status}
                                </span>
                              </td>
                              <td>{po.delivery_date ? formatDateShort(po.delivery_date) : '-'}</td>
                            </tr>
                            <tr className={`expanded-content ${expandedPOs.has(po.id) ? 'expanded' : ''}`}>
                              <td colSpan="7">
                                <div className="line-items-container">
                                  {loadingLines[`po-${po.id}`] ? (
                                    <div className="loading-lines">Loading line items...</div>
                                  ) : poLines[po.id]?.length > 0 ? (
                                    <table className="line-items-table">
                                      <thead>
                                        <tr>
                                          <th>Description</th>
                                          <th>Item #</th>
                                          <th className="text-right">Qty</th>
                                          <th className="text-right">Unit</th>
                                          <th className="text-right">Line</th>
                                          <th>Tax %</th>
                                        </tr>
                                      </thead>
                                      <tbody>
                                        {poLines[po.id].map((line, idx) => (
                                          <tr key={idx}>
                                            <td>{line.description}</td>
                                            <td>{line.itemCode || '-'}</td>
                                            <td className="text-right">{formatQuantity(line.quantity)}</td>
                                            <td className="text-right">{formatPrice(line.unitAmount)}</td>
                                            <td className="text-right">{formatCurrency(line.lineAmount)}</td>
                                            <td>{getTaxPercentDisplay(line.taxType)}</td>
                                          </tr>
                                        ))}
                                      </tbody>
                                    </table>
                                  ) : expandedPOs.has(po.id) && !loadingLines[`po-${po.id}`] ? (
                                    <div className="no-lines">No line items found</div>
                                  ) : null}
                                </div>
                              </td>
                            </tr>
                          </React.Fragment>
                        ))
                      ) : (
                        <tr>
                          <td colSpan="7" className="text-center">
                            No purchase orders found
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>

                {poTotalCount > ITEMS_PER_PAGE && (
                  <PaginationControls
                    pagination={{
                      page: poPage,
                      limit: ITEMS_PER_PAGE,
                      total: poTotalCount,
                      totalPages: Math.ceil(poTotalCount / ITEMS_PER_PAGE)
                    }}
                    onPageChange={setPoPage}
                  />
                )}
              </div>
            )}

            {/* Bills Panel */}
            {activePanel === 'bills' && (
              <div key="bills" className={`panel-content-section ${isPanelOpening ? 'panel-opening' : ''}`}>
                
                <div className="panel-filters">
                  <div className="panel-search">
                    <Input type="text" placeholder="Search bill #, ref, supplier ref..." value={billsSearch} onChange={(e) => { setBillsSearch(e.target.value); setBillsPage(1); }} onEnter={() => fetchBills()} icon="search" />
                    <SearchHelp amounts fields={['bill no.', 'supplier', 'reference', 'supplier ref.', 'account no.', 'PO no.', 'item code', 'product', 'additives', 'total']} />
                  </div>
                  <Dropdown
                    value={billsStatus}
                    onChange={(value) => { setBillsStatus(value); setBillsPage(1); }}
                    options={[
                      { value: '', label: 'All Active' },
                      { value: 'Draft', label: 'Draft' },
                      { value: 'Authorised', label: billStatusLabel('Authorised') },
                      { value: 'Paid', label: 'Paid' },
                      { value: 'Voided', label: 'Voided' }
                    ]}
                  />
                  <DateInput value={billsDateFrom} onChange={(e) => { setBillsDateFrom(e.target.value); setBillsPage(1); }} placeholder="From" />
                  <DateInput value={billsDateTo} onChange={(e) => { setBillsDateTo(e.target.value); setBillsPage(1); }} placeholder="To" />
                </div>

                <div className="panel-toolbar">
                  <div className="toolbar-stats">
                    {billsTotalCount} bill{billsTotalCount !== 1 ? 's' : ''}
                  </div>
                  <div className="toolbar-actions">
                    <button
                      className="btn-secondary btn-sm"
                      onClick={handleExpandAllBills}
                      disabled={expandingAllBills || bills.length === 0}
                    >
                      <span className="material-symbols-rounded">
                        {expandedBills.size === bills.length && bills.length > 0 ? 'unfold_less' : 'unfold_more'}
                      </span>
                      {expandingAllBills ? 'Loading...' : (expandedBills.size === bills.length && bills.length > 0 ? 'Collapse All' : 'Expand All')}
                    </button>
                  </div>
                </div>

                <div className="table-wrapper">
                  <table className="data-table">
                    <thead>
                      <tr>
                        <th style={{ width: '30px' }}></th>
                        <th className="sortable" onClick={() => handleBillSort('bill_date')}>
                          Date {billsSortField === 'bill_date' && (billsSortDirection === 'asc' ? '▲' : '▼')}
                        </th>
                        <th className="sortable" onClick={() => handleBillSort('bill_number')}>
                          Bill # {billsSortField === 'bill_number' && (billsSortDirection === 'asc' ? '▲' : '▼')}
                        </th>
                        <th>Reference</th>
                        <th className="text-right sortable" onClick={() => handleBillSort('total')}>
                          Total {billsSortField === 'total' && (billsSortDirection === 'asc' ? '▲' : '▼')}
                        </th>
                        <th style={{ textAlign: "center" }}>Status</th>
                        <th>Queried</th>
                      </tr>
                    </thead>
                    <tbody>
                      {bills.length > 0 ? (
                        bills.map(bill => (
                          <React.Fragment key={bill.id}>
                            <tr className="expandable-row" onClick={() => toggleBillExpand(bill.id)}>
                              <td className="expand-cell">
                                <span className={`expand-icon ${expandedBills.has(bill.id) ? 'expanded' : ''}`}>▼</span>
                              </td>
                              <td>{formatDateShort(bill.bill_date)}</td>
                              <td>
                                  {/* Opens the bill here rather than in Xero. */}
                                  <span
                                    className="entity-number-link"
                                    onClick={(e) => { e.stopPropagation(); openEntity('bill', bill.id); }}
                                    title={`Open ${bill.bill_number}`}
                                  >
                                    {bill.bill_number}
                                  </span>
                              </td>
                              <td>{bill.reference || bill.supplier_reference || '-'}</td>
                              <td className="text-right">{formatCurrency(bill.total)}</td>
                              <td className="status-col">
                                <span className={`status-badge status-${bill.status?.toLowerCase()}`}>
                                  {billStatusLabel(bill.status)}
                                </span>
                              </td>
                              <td>
                                {bill.is_queried && (
                                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: '2px', color: 'var(--color-warning)', fontSize: 'var(--font-xs)', fontWeight: '600' }}>
                                    <span className="material-symbols-rounded" style={{ fontSize: '14px' }}>help</span>
                                    Queried
                                  </span>
                                )}
                              </td>
                            </tr>
                            <tr className={`expanded-content ${expandedBills.has(bill.id) ? 'expanded' : ''}`}>
                              <td colSpan="7">
                                <div className="line-items-container">
                                  {loadingLines[`bill-${bill.id}`] ? (
                                    <div className="loading-lines">Loading line items...</div>
                                  ) : billLines[bill.id]?.length > 0 ? (
                                    <table className="line-items-table">
                                      <thead>
                                        <tr>
                                          <th>Description</th>
                                          <th>Item #</th>
                                          <th className="text-right">Qty</th>
                                          <th className="text-right">Unit</th>
                                          <th className="text-right">Line</th>
                                          <th>Tax %</th>
                                        </tr>
                                      </thead>
                                      <tbody>
                                        {billLines[bill.id].map((line, idx) => (
                                          <tr key={idx}>
                                            <td>{line.description}</td>
                                            <td>{line.item_code || '-'}</td>
                                            <td className="text-right">{formatQuantity(line.quantity)}</td>
                                            <td className="text-right">{formatPrice(line.unit_amount)}</td>
                                            <td className="text-right">{formatCurrency(line.line_amount)}</td>
                                            <td>{getTaxPercentDisplay(line.tax_type)}</td>
                                          </tr>
                                        ))}
                                      </tbody>
                                    </table>
                                  ) : expandedBills.has(bill.id) && !loadingLines[`bill-${bill.id}`] ? (
                                    <div className="no-lines">No line items found</div>
                                  ) : null}
                                </div>
                              </td>
                            </tr>
                          </React.Fragment>
                        ))
                      ) : (
                        <tr>
                          <td colSpan="7" className="text-center">No bills found</td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>

                {billsTotalCount > ITEMS_PER_PAGE && (
                  <PaginationControls
                    pagination={{
                      page: billsPage,
                      limit: ITEMS_PER_PAGE,
                      total: billsTotalCount,
                      totalPages: Math.ceil(billsTotalCount / ITEMS_PER_PAGE)
                    }}
                    onPageChange={setBillsPage}
                  />
                )}
              </div>
            )}

            {/* Statements Panel */}
            {activePanel === 'statements' && (
              <div key="statements" className={`panel-content-section ${isPanelOpening ? 'panel-opening' : ''}`}>
                <StatementsPanel contactId={contactId} contact={contact} />
              </div>
            )}

            {/* Prescriptions Panel */}
            {activePanel === 'prescriptions' && (
              <div key="prescriptions" className={`panel-content-section ${isPanelOpening ? 'panel-opening' : ''}`}>
                <PrescriptionsPanel
                  prescriptions={prescriptions}
                  loading={prescriptionsLoading}
                  error={prescriptionsError}
                  onChanged={fetchPrescriptions}
                />
              </div>
            )}

            {activePanel === 'formulations' && (
              <div key="formulations" className={`panel-content-section ${isPanelOpening ? 'panel-opening' : ''}`}>
                <CustomerFormulationsPanel
                  contact={{ id: contactId, company_name: contact?.company_name, account_no: contact?.account_no }}
                />
              </div>
            )}

            {activePanel === 'haulage-rates' && (
              <div key="haulage-rates" className={`panel-content-section ${isPanelOpening ? 'panel-opening' : ''}`}>
                <SupplierHaulageRatesPanel supplierId={contactId} supplierName={contact?.company_name || ''} />
              </div>
            )}

            {/* Complaints Panel */}
            {activePanel === 'complaints' && (
              <div key="complaints" className={`panel-content-section ${isPanelOpening ? 'panel-opening' : ''}`}>
                <div className="panel-toolbar">
                  <div className="toolbar-stats">
                    {complaints.length} complaint{complaints.length !== 1 ? 's' : ''}
                  </div>
                </div>

                {complaintsLoading ? (
                  <div className="loading">Loading...</div>
                ) : complaints.length === 0 ? (
                  <div className="empty-state" style={{ padding: 'var(--spacing-xl)', textAlign: 'center', color: 'var(--text-muted)' }}>
                    No complaints for this contact.
                  </div>
                ) : (
                  <div className="table-wrapper">
                    <table className="data-table">
                      <thead>
                        <tr>
                          <th>Number</th>
                          <th>Date</th>
                          <th>Status</th>
                          <th>Subject</th>
                          <th>Rep</th>
                        </tr>
                      </thead>
                      <tbody>
                        {complaints.map(c => (
                          <tr key={c.id} className={c.is_archived ? 'archived' : ''}>
                            <td>{c.complaint_number || '-'}</td>
                            <td style={{ whiteSpace: 'nowrap' }}>
                              {c.complaint_date ? new Date(c.complaint_date).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: '2-digit' }) : '-'}
                            </td>
                            <td>
                              <span className={`status-badge status-${(c.status || '').toLowerCase().replace(/\s+/g, '-')}`}>{c.status || '-'}</span>
                            </td>
                            <td style={{ maxWidth: '260px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={c.complaint_text || ''}>
                              {c.complaint_text || c.reference || '-'}
                            </td>
                            <td>{c.rep_name || '-'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            )}

            {/* Contracts Panel */}
            {activePanel === 'contracts' && (
              <div key="contracts" className={`panel-content-section ${isPanelOpening ? 'panel-opening' : ''}`}>
                <div className="panel-toolbar">
                  <div className="toolbar-stats">
                    {contracts.length} contract{contracts.length !== 1 ? 's' : ''}
                  </div>
                  {periods.length > 0 && (
                    <div className="toolbar-actions" style={{ width: '200px' }}>
                      <Dropdown
                        value={selectedPeriod}
                        onChange={setSelectedPeriod}
                        options={periods.map(p => ({
                          value: p.name,
                          label: p.name
                        }))}
                        placeholder="Select Period"
                      />
                    </div>
                  )}
                </div>

                <div className="table-wrapper">
                  {contractsLoading ? (
                    <div className="loading-state">Loading contracts...</div>
                  ) : (
                    <table className="data-table">
                      <thead>
                        <tr>
                          <th>Type</th>
                          <th>Product</th>
                          <th>Mill</th>
                          <th>Additive</th>
                          <th className="text-right">Qty</th>
                          <th className="text-right">Price</th>
                          <th className="text-right">Cost</th>
                          <th className="text-right">Margin</th>
                          <th>Period</th>
                        </tr>
                      </thead>
                      <tbody>
                        {contracts.length === 0 ? (
                          <tr>
                            <td colSpan="10" className="text-center">
                              No pricing found
                            </td>
                          </tr>
                        ) : (
                          <>
                            {contracts
                              .filter(c => c.category !== 'straights')
                              .map((contract, idx) => (
                                <tr key={`contract-${idx}`}>
                                  <td style={{ textAlign: 'center' }}>
                                    <span className={`status-badge contract-badge status-${contract.category?.toLowerCase() || 'unknown'}`}>
                                      {contract.category ? contract.category.charAt(0).toUpperCase() + contract.category.slice(1) : 'Unknown'}
                                    </span>
                                  </td>
                                  <td>
                                    <div>{contract.product_name}</div>
                                    {contract.product_code && (
                                      <div className="sub-text">{contract.product_code}</div>
                                    )}
                                  </td>
                                  <td>{contract.supplier_name || contract.supplier_company_name || contract.mill || '-'}</td>
                                  <td>
                                    {contract.additive_name ? (
                                      <>
                                        <div>{contract.additive_name}</div>
                                        {contract.additive_code && (
                                          <div className="sub-text">{contract.additive_code}</div>
                                        )}
                                      </>
                                    ) : '-'}
                                  </td>
                                  <td className="text-right">
                                    <div>{Number(contract.quantity) ? formatQuantity(contract.quantity) : '-'}</div>
                                    {contract.quantity > 0 && (() => {
                                      const contracted = parseFloat(contract.quantity);
                                      const used = parseFloat(contract.ordered_quantity || 0);
                                      const remaining = contracted - used;
                                      return (
                                        <div style={{
                                          fontSize: 'var(--font-xs)',
                                          color: remaining < 0 ? 'var(--color-error)' : remaining === 0 ? 'var(--text-secondary)' : 'var(--text-muted)',
                                          fontWeight: remaining < 0 ? '600' : 'normal'
                                        }}>
                                          ({formatQuantity(remaining)})
                                        </div>
                                      );
                                    })()}
                                  </td>
                                  <td className="text-right">{formatPrice(contract.price)}</td>
                                  <td className="text-right">{formatPrice(contract.cost)}</td>
                                  <td className="text-right">{formatCurrency(contract.margin)}</td>
                                  <td>
                                    {contract.is_milk_powder
                                      ? contract.mp_range_label
                                      : contract.category?.toLowerCase() === 'spot' && contract.start_date
                                        ? (() => {
                                            const fmt = (d) => { const dt = new Date(d); return `${dt.getDate().toString().padStart(2,'0')}/${(dt.getMonth()+1).toString().padStart(2,'0')}/${dt.getFullYear().toString().slice(-2)}`; };
                                            return `${fmt(contract.start_date)} - ${fmt(contract.end_date)}`;
                                          })()
                                        : contract.period_name
                                          ? contract.period_name
                                          : contract.start_date
                                            ? `${formatDateShort(contract.start_date)} - ${formatDateShort(contract.end_date)}`
                                            : '-'}
                                  </td>
                                </tr>
                              ))}

                            {(() => {
                              const straightsContracts = contracts.filter(c => c.category === 'straights');
                              if (straightsContracts.length === 0) return null;

                              const productMap = {};
                              straightsContracts.forEach(contract => {
                                const key = `${contract.product_name}-${contract.product_code || ''}`;
                                if (!productMap[key]) {
                                  productMap[key] = {
                                    product_name: contract.product_name,
                                    product_code: contract.product_code,
                                    months: {}
                                  };
                                }
                                const monthKey = `${contract.year}-${contract.month}`;
                                productMap[key].months[monthKey] = {
                                  quantity: contract.quantity,
                                  cost: contract.cost,
                                  price: contract.price,
                                  margin: contract.margin
                                };
                              });

                              const products = Object.values(productMap);

                              return (
                                <React.Fragment>
                                  <tr className="expandable-row" onClick={toggleStraightsExpand}>
                                    <td className="expand-cell">
                                      <span className={`expand-icon ${expandedStraights ? 'expanded' : ''}`}>
                                        ▼
                                      </span>
                                    </td>
                                    <td>
                                      <span className="status-badge contract-badge status-straights">Straights</span>
                                    </td>
                                    <td colSpan="9">
                                      {products.length} product{products.length !== 1 ? 's' : ''}
                                    </td>
                                  </tr>
                                  <tr className={`expanded-content ${expandedStraights ? 'expanded' : ''}`}>
                                    <td colSpan="11">
                                      <div className="line-items-container">
                                        <div className="matrix-table-wrapper">
                                          <table className="matrix-table contracts">
                                            <thead>
                                              <tr>
                                                <th className="sticky-col-1">Product</th>
                                                {getMonthsForPeriod(selectedPeriod).map((m, idx) => (
                                                  <th
                                                    key={`${m.year}-${m.month}`}
                                                    className={`month-header ${idx === 0 ? 'first-scrollable' : ''}`}
                                                    style={{ textAlign: 'center' }}
                                                  >
                                                    {m.label}<br/>{m.year}
                                                  </th>
                                                ))}
                                              </tr>
                                            </thead>
                                            <tbody>
                                              {products.map((product, pIdx) => (
                                                <tr key={pIdx}>
                                                  <td className="sticky-col-1">
                                                    <div>{product.product_name}</div>
                                                    {product.product_code && (
                                                      <div className="sub-text">{product.product_code}</div>
                                                    )}
                                                  </td>
                                                  {getMonthsForPeriod(selectedPeriod).map((m, idx) => {
                                                    const monthKey = `${m.year}-${m.month}`;
                                                    const data = product.months[monthKey];
                                                    return (
                                                      <td
                                                        key={monthKey}
                                                        className={`month-cell ${idx === 0 ? 'first-scrollable' : ''}`}
                                                      >
                                                        {data ? (
                                                          <div className="month-data">
                                                            <div className="price">{formatPrice(data.price)}</div>
                                                            {data.cost && (
                                                              <div className="cost">{formatPrice(data.cost)}</div>
                                                            )}
                                                            {data.quantity && (
                                                              <div className="quantity">{formatQuantity(data.quantity)}</div>
                                                            )}
                                                          </div>
                                                        ) : (
                                                          <span className="no-data">-</span>
                                                        )}
                                                      </td>
                                                    );
                                                  })}
                                                </tr>
                                              ))}
                                            </tbody>
                                          </table>
                                        </div>
                                      </div>
                                    </td>
                                  </tr>
                                </React.Fragment>
                              );
                            })()}
                          </>
                        )}
                      </tbody>
                    </table>
                  )}
                </div>
              </div>
            )}

            {/* Credit Control Panel — gated by `credit_limit` being present in
                the contact response (server strips it for users without
                credit_control permission, so its presence acts as the gate). */}
            {activePanel === 'credit' && creditUsage && contact && 'credit_limit' in contact && (
              <div key="credit" className={`panel-content-section credit-panel ${isPanelOpening ? 'panel-opening' : ''}`}>
                <div className="credit-overview">
                  <div className="credit-header-row">
                    <h4>Credit Limit {creditUsage.hasCreditLimit ? '& Usage' : ''}</h4>
                    {!editingCredit && (
                      <button
                        className="btn-sm set-limit"
                        onClick={() => setEditingCredit(true)}
                      >
                        <span className="material-symbols-rounded">
                          {creditUsage.hasCreditLimit ? 'edit' : 'add'}
                        </span>
                        {creditUsage.hasCreditLimit ? 'Edit' : 'Set Limit'}
                      </button>
                    )}
                  </div>

                  {editingCredit ? (
                    <div className="credit-edit-form">
                      <div className="form-row">
                        <label>Credit Limit (£)</label>
                        <input
                          type="number"
                          value={newCreditLimit}
                          onChange={(e) => setNewCreditLimit(e.target.value)}
                          step="0.01"
                          placeholder="Enter credit limit..."
                        />
                      </div>
                      <div className="form-row">
                        <label>Notes</label>
                        <textarea
                          value={creditNotes}
                          onChange={(e) => setCreditNotes(e.target.value)}
                          rows="2"
                          placeholder="Optional notes..."
                        />
                      </div>
                      <div className="form-actions">
                        <button className="btn-round" onClick={handleUpdateCreditLimit}>
                          <div className='icon-wrapper'>
                            <span className="material-symbols-rounded">save</span>
                          </div>
                        </button>
                        <button className="btn-round btn-danger" onClick={() => {
                          setEditingCredit(false);
                          setNewCreditLimit(creditUsage?.creditLimit || '');
                          setCreditNotes('');
                        }}>
                          <div className='icon-wrapper'>
                            <span className="material-symbols-rounded">close</span>
                          </div>
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div className="credit-details">
                      <div className="credit-row">
                        <span className="credit-label">Credit Limit</span>
                        <span className="credit-value">
                          {creditUsage.hasCreditLimit ? formatCurrency(creditUsage.creditLimit) : 'Not set'}
                        </span>
                      </div>

                      {creditUsage.hasCreditLimit && (
                        <>
                          <div className="credit-row">
                            <span className="credit-label">Unpaid Invoices</span>
                            <span className="credit-value">{formatCurrency(creditUsage.unpaidInvoicesTotal)}</span>
                          </div>
                          <div className="credit-row">
                            <span className="credit-label">Open Purchase Orders</span>
                            <span className="credit-value">{formatCurrency(creditUsage.openPurchaseOrdersTotal)}</span>
                          </div>
                          <div className="credit-row">
                            <span className="credit-label">Unallocated Credits</span>
                            <span className="credit-value credit-positive">
                              {formatCurrency(creditUsage.unallocatedCreditsTotal)}
                            </span>
                          </div>
                          <div className="credit-row highlight">
                            <span className="credit-label">Total Exposure</span>
                            <span className="credit-value">{formatCurrency(creditUsage.totalExposure)}</span>
                          </div>
                          <div className="credit-row highlight">
                            <span className="credit-label">Available Credit</span>
                            <span className="credit-value" style={{
                              color: creditUsage.availableCredit < 0 ? 'var(--color-error)' : 'var(--color-success)'
                            }}>
                              {formatCurrency(creditUsage.availableCredit)}
                            </span>
                          </div>

                          <div className="utilization-section">
                            <div className="utilization-bar">
                              <div
                                className="utilization-fill"
                                style={{
                                  width: `${Math.min(creditUsage.utilizationPercentage, 100)}%`,
                                  backgroundColor: getUtilizationColor(creditUsage.utilizationPercentage)
                                }}
                              />
                            </div>
                            <div className="utilization-info">
                              <span style={{ color: getUtilizationColor(creditUsage.utilizationPercentage) }}>
                                {creditUsage.utilizationPercentage.toFixed(1)}% Utilised
                              </span>
                              {creditUsage.isOverLimit && (
                                <span className="over-limit-badge">
                                  <span className="material-symbols-rounded">warning</span>
                                  OVER LIMIT
                                </span>
                              )}
                            </div>
                          </div>
                        </>
                      )}

                      {creditUsage.creditGroup?.isGroup && (
                        <div className="credit-group-block">
                          <div className="credit-group-title">
                            <span className="material-symbols-rounded">account_tree</span>
                            Shared across {creditUsage.creditGroup.memberCount} linked accounts
                          </div>
                          <div className="credit-group-members">
                            {[...creditUsage.creditGroup.members]
                              .sort((a, b) => (Number(b.isRoot) - Number(a.isRoot)) || (b.exposure - a.exposure))
                              .map(m => (
                                <div key={m.id} className="credit-group-member">
                                  <span className="cgm-name">
                                    <span className={`cgm-role ${m.isRoot ? 'cgm-parent' : 'cgm-child'}`}>
                                      {m.isRoot ? 'PARENT' : 'CHILD'}
                                    </span>
                                    {m.companyName}
                                  </span>
                                  <span className="cgm-exposure">{formatCurrency(m.exposure)}</span>
                                </div>
                              ))}
                          </div>
                          <div className="credit-group-note">Exposure and payment behaviour below are combined across these accounts.</div>
                        </div>
                      )}
                    </div>
                  )}
                </div>

                <div className="credit-payments-section">
                  <h3 className="credit-payments-heading">Payment Behaviour</h3>
                  <PaymentBehaviourPanel contactId={contactId} contactName={contact?.company_name} />
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Audit History Modal */}
      <AuditHistoryModal
        isOpen={showHistory}
        onClose={() => setShowHistory(false)}
        entityType="contact"
        entityId={contact?.id}
        entityIdentifier={contact?.account_no || contact?.company_name}
      />
    </div>

    {/* Internal edit modal — only when the parent didn't supply onEdit. Rendered
        as a sibling of the overlay (not a child) so its backdrop clicks don't
        bubble through the React tree and close the underlying view modal.
        On save we re-fetch so the view reflects the edits without closing. */}
    {showInternalEdit && contact && (
      <ContactEditModal
        contact={contact}
        onClose={() => setShowInternalEdit(false)}
        onSave={() => { setShowInternalEdit(false); fetchContactDetails(); }}
      />
    )}

    {/* Sibling of the overlay for the same reason as the edit modal above: a
        backdrop click must not bubble through and close the view behind it. */}
    {showNotesEdit && contact && (
      <ContactNotesModal
        contact={contact}
        onClose={() => setShowNotesEdit(false)}
        onSaved={() => fetchContactDetails()}
      />
    )}
    </>,
    document.body
  );
}

export default ContactModal;
